// =============================================================================
// oauthCallback — what the provider sent back to /auth/callback, as one
// operator sentence.
//
// The URL is attacker-shaped: anyone can send a link to
// /auth/callback?error_description=Call+this+number+to+unlock+your+account.
// So nothing from it is ever rendered as prose. Errors map onto OUR copy; the
// only thing echoed back is the error CODE, and only when it is a bare
// snake_case word.
// =============================================================================

/** The marker the before_user_created hook (mig 20261034000000) puts in its
 *  refusal. GoTrue carries the hook's message back as error_description; we
 *  match this token and show our own sentence. oauthCallback.test.ts pins it
 *  against the migration text, so the two cannot drift apart unnoticed. */
export const NO_ACCOUNT_MARKER = '[oauth_no_account]'

/** The OTHER no-account refusal, and the one prod actually sends. With public
 *  sign-up closed (`disable_signup`, #3010), GoTrue refuses a new provider user
 *  BEFORE the hook is asked (`external.go`, `case models.CreateAccount`) and
 *  redirects with `error=access_denied&error_code=signup_disabled`. Read only
 *  `error`, that is indistinguishable from a cancel at Google — which is what
 *  the page said on 5 Oct, so an uninvited user was told they had cancelled. */
const SIGNUP_DISABLED_CODE = 'signup_disabled'

export type OAuthCallback =
  | { kind: 'code'; code: string }
  | { kind: 'error'; message: string }

const SAFE_CODE = /^[a-z_]{1,40}$/

/** Read `?code=` / `?error=` from the query AND the hash — GoTrue puts PKCE
 *  results in the query, but an implicit-flow style error can arrive in the hash. */
export function readOAuthCallback(search: string, hash: string): OAuthCallback {
  const q = new URLSearchParams(search)
  const h = new URLSearchParams(hash.replace(/^#/, ''))
  const get = (k: string) => q.get(k) ?? h.get(k)

  const code = get('code')
  const error = get('error') ?? get('error_code')
  if (code && !error) return { kind: 'code', code }

  if (!error) {
    return { kind: 'error', message: 'This page only finishes a sign-in. Go back to sign-in to start one.' }
  }
  if ((get('error_description') ?? '').includes(NO_ACCOUNT_MARKER)
      || get('error_code') === SIGNUP_DISABLED_CODE) {
    return {
      kind: 'error',
      message: "There's no account for that email address here. Sign in with the email your admin " +
               'invited, or ask your admin to invite the address you just used.',
    }
  }
  if (error === 'access_denied') {
    return { kind: 'error', message: 'Sign-in was cancelled or refused. Nothing was changed.' }
  }
  const shown = SAFE_CODE.test(error) ? ` (${error})` : ''
  return { kind: 'error', message: `Sign-in didn't finish${shown}. Go back to sign-in and try again.` }
}

/** The sentence for a failed code exchange (authDirect's exchangeOAuthCodeDirect). */
export function exchangeFailureText(reason: 'no_verifier' | 'rejected' | 'network'): string {
  switch (reason) {
    case 'no_verifier':
      return 'This sign-in was started in a different browser or app. Go back to sign-in and start it again here.'
    case 'rejected':
      return 'This sign-in has expired or was already used. Go back to sign-in and try again.'
    case 'network':
      return "Couldn't reach the server to finish signing in. Check the connection and try again."
  }
}
